import type { PositionCloseKind, PositionSemantics, Trade } from "../types/trade";

export type { PositionCloseKind, PositionSemantics };

export interface PositionSemanticsBadge {
  key: string;
  label: string;
  tooltip: string;
  gradient: string;
}

function toNumberOrNull(value: unknown): number | null {
  if (value == null || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function toStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.map((item) => String(item)).filter(Boolean);
}

/** Parse API `position_semantics` without client-side inference. */
export function parsePositionSemantics(raw: unknown): PositionSemantics | undefined {
  if (!raw || typeof raw !== "object") return undefined;
  const o = raw as Record<string, unknown>;
  const closeKind = o.close_kind;
  const parsedCloseKind: PositionCloseKind =
    closeKind === "full" || closeKind === "partial" ? closeKind : null;

  return {
    close_kind: parsedCloseKind,
    closed_quantity: toNumberOrNull(o.closed_quantity),
    original_entry_quantity: toNumberOrNull(o.original_entry_quantity),
    remaining_quantity: toNumberOrNull(o.remaining_quantity),
    lot_key: o.lot_key != null ? String(o.lot_key) : null,
    is_remainder_open: Boolean(o.is_remainder_open),
    is_dust: Boolean(o.is_dust),
    is_scratch: Boolean(o.is_scratch),
    shares_exit_fill: Boolean(o.shares_exit_fill),
    related_trade_ids: toStringArray(o.related_trade_ids),
  };
}

function formatQty(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return "—";
  return String(value);
}

export function normalizeTradeStatus(
  status: unknown
): "open" | "closed" | string {
  const normalized = String(status ?? "").trim().toLowerCase();
  if (normalized === "open" || normalized === "closed") return normalized;
  return normalized;
}

type TradeOpenProbe = Pick<
  Trade,
  "status" | "exitDate" | "broker_synced" | "positionSemantics"
> & {
  id?: string;
};

export function isTradeOpen(trade: TradeOpenProbe, brokerOpenTradeIds?: ReadonlySet<string>): boolean {
  if (trade.id && brokerOpenTradeIds?.has(trade.id)) return true;
  if (normalizeTradeStatus(trade.status) === "open") return true;
  if (trade.positionSemantics?.is_remainder_open) return true;
  // Broker sync sometimes persists live positions as closed without an exit timestamp.
  if (trade.broker_synced) {
    const exit = trade.exitDate;
    if (exit == null || String(exit).trim() === "") return true;
  }
  return false;
}

export function isTradeClosed(
  trade: TradeOpenProbe,
  brokerOpenTradeIds?: ReadonlySet<string>
): boolean {
  return !isTradeOpen(trade, brokerOpenTradeIds);
}

const GRADIENT_PRIMARY = "linear-gradient(135deg, #38BBD1, #3EEAEC)";
const GRADIENT_GLACIAL_PURPLE = "linear-gradient(135deg, #6C4BFF, #B9A7FF)";
const GRADIENT_GLACIAL_GREY = "linear-gradient(135deg, #575757, #D3D3D3)";
const GRADIENT_SPLIT_EXIT_PURPLE = "linear-gradient(135deg, #805AD5, #B794F4)";
const GRADIENT_GLACIAL_ORANGE = "linear-gradient(135deg, #F45B0B, #F49113)";
const GRADIENT_CYAN = "linear-gradient(135deg, #38BBD1, #3EEAEC)";

/**
 * Single lifecycle badge for the trade log Status column: Open | Open remainder | Closed | Partial close.
 */
export function getTradeLogPrimaryStatusBadge(
  trade: Trade,
  brokerOpenTradeIds?: ReadonlySet<string>
): PositionSemanticsBadge {
  const ps = trade.positionSemantics;

  if (isTradeOpen(trade, brokerOpenTradeIds)) {
    if (ps?.is_remainder_open) {
      return {
        key: "remainder-open",
        label: "Open remainder",
        tooltip:
          "Open slice left after a partial close on the same entry lot. P&L is realized on the closed rows.",
        gradient: GRADIENT_GLACIAL_ORANGE,
      };
    }
    return {
      key: "open",
      label: "Open",
      tooltip: "Position is still open.",
      gradient: GRADIENT_GLACIAL_ORANGE,
    };
  }

  if (ps?.close_kind === "partial") {
    return {
      key: "partial-close",
      label: "Partial close",
      tooltip: `Closed ${formatQty(ps.closed_quantity)} of ${formatQty(
        ps.original_entry_quantity
      )} on this lot. Remaining size: ${formatQty(ps.remaining_quantity)}.`,
      gradient: GRADIENT_CYAN,
    };
  }

  return {
    key: "closed",
    label: "Closed",
    tooltip: "Position is fully closed.",
    gradient: GRADIENT_PRIMARY,
  };
}

/** Open rows (including dust) stay pinned to the top of the trade log table. */
export function isTradeLogPinnedOpen(trade: TradeOpenProbe): boolean {
  return isTradeOpen(trade);
}

/** Semantics badges shown alongside the primary status (excludes redundant open/close variants). */
export function getPositionSemanticsSecondaryBadges(trade: Trade): PositionSemanticsBadge[] {
  const primaryKey = getTradeLogPrimaryStatusBadge(trade).key;
  return getPositionSemanticsBadges(trade).filter((badge) => {
    if (badge.key === primaryKey) return false;
    if (primaryKey === "closed" && badge.key === "full-close") return false;
    return true;
  });
}

/** Badges/tooltips derived only from `position_semantics` (no FE guessing). */
export function getPositionSemanticsBadges(trade: Trade): PositionSemanticsBadge[] {
  const ps = trade.positionSemantics;
  if (!ps) return [];

  const badges: PositionSemanticsBadge[] = [];
  const glacialPurple = GRADIENT_GLACIAL_PURPLE;
  const glacialGrey = GRADIENT_GLACIAL_GREY;
  const splitExitPurple = GRADIENT_SPLIT_EXIT_PURPLE;
  const orange = GRADIENT_GLACIAL_ORANGE;
  const cyan = GRADIENT_CYAN;

  if (ps.is_remainder_open) {
    badges.push({
      key: "remainder-open",
      label: "Open remainder",
      tooltip:
        "Open slice left after a partial close on the same entry lot. P&L is realized on the closed rows.",
      gradient: orange,
    });
  }

  if (ps.close_kind === "partial") {
    badges.push({
      key: "partial-close",
      label: "Partial close",
      tooltip: `Closed ${formatQty(ps.closed_quantity)} of ${formatQty(
        ps.original_entry_quantity
      )} on this lot. Remaining size: ${formatQty(ps.remaining_quantity)}.`,
      gradient: cyan,
    });
  }

  if (ps.shares_exit_fill) {
    badges.push({
      key: "split-exit",
      label: "Split exit",
      tooltip:
        "This exit fill is shared across multiple closed rows (Binance FIFO sell split).",
      gradient: splitExitPurple,
    });
  }

  if (ps.is_dust) {
    badges.push({
      key: "dust",
      label: "Dust",
      tooltip: "Open remainder below the dust threshold (small notional).",
      gradient: glacialGrey,
    });
  }

  if (ps.is_scratch) {
    badges.push({
      key: "scratch",
      label: "Scratch",
      tooltip: "Closed with zero net P&L (fees/rounding).",
      gradient: glacialPurple,
    });
  }

  if (ps.close_kind === "full" && badges.length === 0) {
    badges.push({
      key: "full-close",
      label: "Full close",
      tooltip: "Entire entry lot was closed on this row.",
      gradient: cyan,
    });
  }

  return badges;
}

/** Closed rows with exit timestamp (includes partial closes without net P&L yet). */
export function isClosedTradeWithExit(trade: Trade): boolean {
  return isTradeClosed(trade) && trade.exitDate != null;
}

/** Rows that contribute a finite realized net P&L (intraday chart + day P&L sum). */
export function contributesRealizedDayPnl(trade: Trade): boolean {
  return isClosedTradeWithExit(trade) && typeof trade.netPnL === "number" && Number.isFinite(trade.netPnL);
}

export function computeTradingDayStats(trades: Trade[]): {
  totalTrades: number;
  closedTradeCount: number;
  openTradeCount: number;
  dayPnl: number;
  winRate: number;
  winRateSample: number;
  expectancy: number;
  expectancySample: number;
} {
  const closed = trades.filter(isClosedTradeWithExit);
  const open = trades.filter((t) => isTradeOpen(t));
  const withPnl = trades.filter(contributesRealizedDayPnl);
  const dayPnl = withPnl.reduce((sum, t) => sum + (t.netPnL ?? 0), 0);
  const wins = withPnl.filter((t) => (t.netPnL ?? 0) > 0).length;
  const losses = withPnl.filter((t) => (t.netPnL ?? 0) < 0).length;
  const winRateSample = wins + losses;
  const winRate = winRateSample > 0 ? (wins / winRateSample) * 100 : 0;
  const expectancySample = withPnl.length;
  const expectancy = expectancySample > 0 ? dayPnl / expectancySample : 0;

  return {
    totalTrades: trades.length,
    closedTradeCount: closed.length,
    openTradeCount: open.length,
    dayPnl,
    winRate,
    winRateSample,
    expectancy,
    expectancySample,
  };
}
